import React, { useState, useEffect } from 'react';
import { handleApiError } from '../utils/errorHandler';
import axios from 'axios';

// Appointment types with icons and colors
const appointmentTypes = [
  { value: 'doctor_visit', label: 'Doctor Visit', icon: '👩‍⚕️', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'colonoscopy', label: 'Colonoscopy', icon: '🔬', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'stool_test', label: 'Stool Test', icon: '🧪', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'blood_test', label: 'Blood Test', icon: '🩸', color: 'bg-red-100 text-red-700 border-red-200' },
];

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    doctor_name: '',
    appointment_type: '',
    specialty: '',
    appointment_date: '',
    location: '',
    phone: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/appointments');
      setAppointments(response.data);
    } catch (error) {
      handleApiError(error, 'Failed to fetch appointments');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAppointment) {
        await axios.put(`http://localhost:5000/api/appointments/${editingAppointment.id}`, formData);
      } else {
        await axios.post('http://localhost:5000/api/appointments', formData);
      }
      
      resetForm();
      fetchAppointments();
    } catch (error) {
      handleApiError(error, 'Failed to save appointment');
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      doctor_name: appointment.doctor_name,
      appointment_type: appointment.appointment_type || '',
      specialty: appointment.specialty || '',
      appointment_date: appointment.appointment_date ? appointment.appointment_date.slice(0, 16) : '',
      location: appointment.location || '',
      phone: appointment.phone || '',
      email: appointment.email || '',
      notes: appointment.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await axios.delete(`http://localhost:5000/api/appointments/${id}`);
        fetchAppointments();
      } catch (error) {
        handleApiError(error, 'Failed to delete appointment');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      doctor_name: '',
      appointment_type: '',
      specialty: '',
      appointment_date: '',
      location: '',
      phone: '',
      email: '',
      notes: ''
    });
    setEditingAppointment(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const exportToGoogleCalendar = (appointment) => {
    const startDate = new Date(appointment.appointment_date);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
    
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const title = encodeURIComponent(`${appointment.doctor_name} - ${appointment.appointment_type || 'Appointment'}`);
    const details = encodeURIComponent(
      `Doctor: ${appointment.doctor_name}\n` +
      `Type: ${appointment.appointment_type || 'N/A'}\n` +
      `Specialty: ${appointment.specialty || 'N/A'}\n` +
      `Phone: ${appointment.phone || 'N/A'}\n` +
      `Email: ${appointment.email || 'N/A'}\n` +
      `Notes: ${appointment.notes || 'N/A'}`
    );
    const location = encodeURIComponent(appointment.location || '');
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&details=${details}&location=${location}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  const getAppointmentTypeInfo = (type) => {
    return appointmentTypes.find(t => t.value === type) || { label: type, icon: '📅', color: 'bg-gray-100 text-gray-700 border-gray-200' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="glass-card p-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 border-4 border-primary-400/30 border-t-primary-500 rounded-full animate-spin"></div>
            <p className="text-neutral-600 font-medium">Loading appointments...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 fade-in">
      {/* Hero Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gradient mb-3">👩‍⚕️ Doctor Appointments</h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          Manage your healthcare appointments and export them to Google Calendar for easy scheduling.
        </p>
      </div>

      <div className="glass-card-strong p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gradient">Your Appointments</h2>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Appointment
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Enhanced backdrop */}
            <div 
              className="absolute inset-0 bg-black/30 backdrop-blur-xl"
              onClick={resetForm}
            />
            
            {/* Premium modal content */}
            <div className="relative w-full max-w-2xl glass-card-strong p-8 scale-in max-h-[90vh] overflow-y-auto premium-scrollbar">
              {/* Header with gradient text */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gradient">
                    {editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
                  </h2>
                  <p className="text-sm text-neutral-600 mt-1">
                    {editingAppointment ? 'Update appointment details' : 'Add a new healthcare appointment'}
                  </p>
                </div>
                <button
                  onClick={resetForm}
                  className="p-2 rounded-2xl text-neutral-400 hover:text-neutral-600 hover:bg-glass-white transition-all duration-200 hover:scale-110 group"
                >
                  <svg className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Premium Form */}
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Appointment Type Section */}
                <div className="glass-card p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
                    <span>📅</span>
                    <span>Appointment Type</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {appointmentTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, appointment_type: type.value })}
                        className={`
                          p-3 rounded-2xl border-2 transition-all duration-300 text-center hover:scale-105
                          ${formData.appointment_type === type.value
                            ? 'bg-primary-gradient text-white border-primary-300 shadow-premium'
                            : 'bg-glass-white border-neutral-200 text-neutral-700 hover:border-primary-300'
                          }
                        `}
                      >
                        <div className="text-2xl mb-1">{type.icon}</div>
                        <div className="text-xs font-medium">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Basic Information Section */}
                <div className="glass-card p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
                    <span>👩‍⚕️</span>
                    <span>Doctor Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Doctor Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.doctor_name}
                        onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                        className="input-glass"
                        placeholder="Dr. Smith"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Specialty
                      </label>
                      <input
                        type="text"
                        value={formData.specialty}
                        onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                        className="input-glass"
                        placeholder="e.g., Gastroenterologist"
                      />
                    </div>
                  </div>
                </div>

                {/* Date & Time Section */}
                <div className="glass-card p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
                    <span>📅</span>
                    <span>Date & Time</span>
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Appointment Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.appointment_date}
                      onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                      className="input-glass"
                    />
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="glass-card p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
                    <span>📞</span>
                    <span>Contact Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-glass"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-glass"
                        placeholder="doctor@clinic.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Location/Address
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="input-glass"
                      placeholder="123 Medical Center Dr, City, State"
                    />
                  </div>
                </div>

                {/* Notes Section */}
                <div className="glass-card p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
                    <span>📝</span>
                    <span>Notes</span>
                  </h3>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="textarea-glass"
                    placeholder="Additional notes or reminders..."
                  />
                </div>

                {/* Premium Action Buttons */}
                <div className="flex space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1 relative overflow-hidden group"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{editingAppointment ? 'Update Appointment' : 'Add Appointment'}</span>
                    </div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="grid gap-6">
          {appointments.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-6 bg-teal-100/50 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <span className="text-4xl">📅</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-700 mb-2">No appointments yet</h3>
              <p className="text-neutral-500 mb-6">Click "Add Appointment" to schedule your first healthcare appointment.</p>
              <button 
                onClick={() => setShowForm(true)}
                className="btn-primary"
              >
                Add Your First Appointment
              </button>
            </div>
          ) : (
            appointments.map((appointment) => {
              const typeInfo = getAppointmentTypeInfo(appointment.appointment_type);
              return (
                <div key={appointment.id} className="glass-card p-6 hover-lift group border-l-4 border-primary-400">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="p-2 bg-primary-gradient rounded-full">
                          <span className="text-xl">{typeInfo.icon}</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-neutral-800">
                            Dr. {appointment.doctor_name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            {appointment.appointment_type && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                            )}
                            {appointment.specialty && (
                              <span className="bg-neutral-100 text-neutral-700 px-2 py-1 rounded-full text-xs font-medium">
                                {appointment.specialty}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3 text-neutral-600">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-neutral-700 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Date:
                          </span>
                          <span className="font-semibold text-primary-700">{formatDate(appointment.appointment_date)}</span>
                        </div>
                        
                        {appointment.location && (
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-neutral-700 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              Location:
                            </span>
                            <span>{appointment.location}</span>
                          </div>
                        )}
                        
                        {appointment.phone && (
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-neutral-700 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              Phone:
                            </span>
                            <span>{appointment.phone}</span>
                          </div>
                        )}
                        
                        {appointment.email && (
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-neutral-700 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Email:
                            </span>
                            <span>{appointment.email}</span>
                          </div>
                        )}
                        
                        {appointment.notes && (
                          <div className="flex items-start space-x-3">
                            <span className="font-medium text-neutral-700 flex items-center mt-1">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                              </svg>
                              Notes:
                            </span>
                            <span className="bg-neutral-50 p-2 rounded-lg text-sm">{appointment.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2 ml-6">
                      <button
                        onClick={() => exportToGoogleCalendar(appointment)}
                        className="btn-secondary text-xs px-3 py-2 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
                        title="Export to Google Calendar"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Google Cal
                      </button>
                      <button
                        onClick={() => handleEdit(appointment)}
                        className="btn-secondary text-xs px-3 py-2 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
                        title="Edit appointment"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(appointment.id)}
                        className="btn-secondary text-xs px-3 py-2 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                        title="Delete appointment"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Appointments;